interface SimpleFilter {
  field: string;
  operator: "equals" | "contains";
  value: unknown;
}

type FilterGroup = {
  AND?: (Filter | FilterGroup)[];
  OR?: (Filter | FilterGroup)[];
};

type Filter = SimpleFilter | FilterGroup;

interface UseDataGridClientFilterProps<T = any> {
  filters: Filter | Filter[];
  data: T;
}

export const useTableFilter = ({
  data,
  filters,
}: UseDataGridClientFilterProps) => {
  if (!Array.isArray(data)) return [];

  return data.filter((row) => {
    // If filters is an array but not wrapped in AND/OR, treat it as an AND group
    if (Array.isArray(filters)) {
      return evaluateAndGroup(row, filters);
    }
    // Otherwise evaluate the filter/filter group
    return evaluateFilter(row, filters);
  });
};

const evaluateFilter = (row: any, filter: Filter): boolean => {
  if ("field" in filter && "operator" in filter) {
    const simpleFilter = filter as SimpleFilter;
    const isFieldExist = Reflect.has(row, simpleFilter.field);
    if (!isFieldExist) return false;

    const value = row[simpleFilter.field];
    switch (simpleFilter.operator) {
      case "equals":
        return value === simpleFilter.value;
      case "contains":
        return typeof value === "string" &&
          typeof simpleFilter.value === "string"
          ? value.toLowerCase().includes(simpleFilter.value.toLowerCase())
          : value.includes(simpleFilter.value);
      default:
        return false;
    }
  }

  if ("AND" in filter && Array.isArray(filter.AND)) {
    return evaluateAndGroup(row, filter.AND);
  }

  if ("OR" in filter && Array.isArray(filter.OR)) {
    return evaluateOrGroup(row, filter.OR);
  }

  return false;
};

const evaluateAndGroup = (
  row: any,
  filters: (Filter | FilterGroup)[],
): boolean => {
  return filters.every((filter) => evaluateFilter(row, filter));
};

const evaluateOrGroup = (
  row: any,
  filters: (Filter | FilterGroup)[],
): boolean => {
  return filters.some((filter) => evaluateFilter(row, filter));
};
